import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button, Card, Collapse, Input, InputNumber, Modal, Popconfirm, Select, Space,
  Statistic, Table, Tag, Tooltip, Typography, message,
} from "antd";
import { DeleteOutlined, EditOutlined, FileExcelOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import Alert from "../../components/GxAlert";
import GxNote from "../../components/GxNote";
import { createAdvanceBatch, deleteFinanceTxn, downloadPaymentFile, getBalances, getFinanceTxns,
         setTxnPaymentStatus, updateAdvanceBatch,
         updateFinanceTxn } from "../../api/client";
import { apiError } from "../../utils/apiError";
import { JalaliDateInput, todayISO, toJalali } from "../../utils/dateUtils";
import { faNum } from "../../utils/faNumberFormat";
import { MoneyInput } from "../../utils/money";
import { filterByChildren } from "../../utils/selectFilter";
import SourceSelect, { OFFICE, loadPettyAccounts, sourceLabel, sourceOf } from "../../components/SourceSelect";
import CopyText from "../../components/CopyText";

const { Text } = Typography;

const METHOD_LABEL = { cash: "نقدی", bank_transfer: "کارت‌به‌کارت / حواله", check: "چک" };

/**
 * مساعدهٔ دسته‌جمعی — انتخابِ نفرات، مبلغ‌ها، یک لیستِ نهایی برای پرداخت.
 *
 * چرا جدا از «هزینه‌ها و تراکنش‌ها»: مساعده یک **رویدادِ گروهی** است؛ یک‌بار
 * تصمیم گرفته و یک‌بار پرداخت می‌شود. ثبتِ تک‌تک هم کند است و هم نیمه‌کاره
 * می‌ماند، آن‌وقت لیستِ دستِ صندوق‌دار با دفترِ مالی نمی‌خواند. ثبت **تراکنشی**
 * است: یا همهٔ ردیف‌ها می‌نشینند یا هیچ‌کدام.
 */
export default function AdvanceBatchTab({ employees = [], periodId, onDone }) {
  /* 🔴 تا امروز، ثبتِ دسته‌جمعی فقط تراکنش می‌ساخت و **هیچ ردی نشان نمی‌داد**:
     کاربر نمی‌دید چه مساعده‌ای ثبت کرده و نمی‌توانست فایلِ پرداختش را دوباره
     بگیرد. این بخش همان ثبت‌شده‌ها را per **تاریخ** گروه می‌کند — چون مساعده
     در کارگاه یک‌جا تصمیم و یک‌جا پرداخت می‌شود، پس «دسته» = همان روز. */
  const [saved, setSaved] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const loadSaved = useCallback(() => {
    setLoadingSaved(true);
    getFinanceTxns({ type: "advance" })
      .then((r) => setSaved(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingSaved(false));
  }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const batches = useMemo(() => {
    const m = new Map();
    (saved || []).forEach((t) => {
      const k = String(t.occurred_at || "").slice(0, 10);
      if (!m.has(k)) m.set(k, { date: k, rows: [], total: 0, pending: 0 });
      const g = m.get(k);
      g.rows.push(t);
      g.total += Number(t.amount) || 0;
      if (t.payment_status !== "paid") g.pending = (g.pending || 0) + 1;
    });
    // جدیدترین بالا — همان ترتیبی که کاربر انتظار دارد
    return [...m.values()].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [saved]);

  /* «پرداخت شد / نشد» per نفر.
     قاعدهٔ حسابداری: لیستِ مساعده روزِ تصمیم ساخته می‌شود ولی پول ممکن است
     فردا داده شود. تا وقتی «پرداخت شد» نخورد، سند در حسابِ شخص **نمی‌نشیند**
     — وگرنه کسی که هنوز پولش را نگرفته بدهکار نشان داده می‌شود. */
  const [marking, setMarking] = useState(false);
  const mark = async (ids, paid, when, source) => {
    if (!ids.length) return;
    setMarking(true);
    try {
      // مبدأ فقط اگر کاربر در همین پنجره عوضش کرده باشد فرستاده می‌شود؛ وگرنه مبدأِ خودِ سند می‌ماند
      const r = await setTxnPaymentStatus({ ids, paid, paid_at: when || todayISO(), ...(source || {}) });
      message.success(r.data?.message || "ثبت شد");
      loadSaved(); onDone?.();
    } catch (e) { message.error(apiError(e, "ثبت نشد")); }
    setMarking(false);
  };

  /* تاریخِ پرداخت پرسیده می‌شود، فرض نمی‌شود. پیش‌فرض «امروز» است چون در
     بیشترِ مواقع همان است، ولی پول ممکن است دیروز داده شده و امروز ثبت شود؛
     تاریخِ غلط یعنی صورت‌حسابِ بازه‌ایِ غلط. */
  const [payAsk, setPayAsk] = useState(null);   // {ids, when, count, current, source?}
  const askPay = (ids) => {
    const first = saved.find((x) => x.id === ids[0]);
    setPayAsk({ ids, when: todayISO(), count: ids.length, current: sourceOf(first), source: null });
  };
  useEffect(() => { loadPettyAccounts(); }, []);

  /* ویرایشِ یک ردیفِ مساعده — تا امروز فقط «پرداخت شد» و «فایل پرداخت» بود و
     مبلغِ اشتباه هیچ راهِ اصلاحی نداشت جز حذف و ثبتِ دوباره. */
  const [editRow, setEditRow] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const saveEdit = async () => {
    if (!(editRow?.amount > 0)) return message.warning("مبلغ باید بزرگ‌تر از صفر باشد");
    if (!editRow.occurred_at) return message.warning("تاریخ لازم است");
    setSavingEdit(true);
    try {
      await updateFinanceTxn(editRow.id, {
        amount: Number(editRow.amount),
        occurred_at: editRow.occurred_at,
        description: editRow.description || null,
      });
      message.success("ویرایش شد");
      setEditRow(null); loadSaved(); onDone?.();
    } catch (e) { message.error(apiError(e, "ویرایش نشد")); }
    setSavingEdit(false);
  };

  // حذفِ تکیِ یک مساعده (حذفِ نرم با سابقه؛ حرکتِ تنخواه و حقوقِ ماهِ باز هم هم‌تراز می‌شوند)
  const removeOne = async (r) => {
    try {
      const res = await deleteFinanceTxn(r.id);
      message.success(res.data?.message || "حذف شد");
      loadSaved(); onDone?.();
    } catch (e) { message.error(apiError(e, "حذف نشد")); }
  };

  const [reDownloading, setReDownloading] = useState(null);
  const reFile = async (g) => {
    setReDownloading(g.date);
    try {
      await downloadPaymentFile({
        title: `لیست پرداخت مساعده — ${toJalali(g.date)}`,
        subtitle: `${g.rows.length} نفر`,
        items: sortedRows(g).map((t) => ({
          employee_id: t.employee_id, amount: t.amount, note: t.description || "",
        })),
        // مرتب‌شده ⇒ دقیقاً همان ترتیب (بدونِ دو بخشِ بیمه‌ای/غیربیمه‌ای؛ ستونِ «بیمه» هست)
        grouped: !sortBy[g.date]?.order,
      });
      loadSaved();
      message.success("فایلِ پرداخت ساخته شد");
    } catch (e) { message.error(apiError(e, "ساختِ فایل ناموفق بود")); }
    setReDownloading(null);
  };

  /* حذفِ کلِ یک دستهٔ اشتباه — همان PUTِ ویرایشِ دسته با لیستِ خالی؛ پس همان
     قواعد: تراکنشی، حذفِ نرم، و ردیفِ پرداخت‌شده تا «برگرداندن» نخورد نمی‌رود. */
  const [deletingBatch, setDeletingBatch] = useState(null);
  const deleteBatch = async (g) => {
    setDeletingBatch(g.date);
    try {
      const r = await updateAdvanceBatch({
        original_date: g.date, occurred_at: g.date,
        payment_method: g.rows[0]?.payment_method || null,
        reference_number: g.rows[0]?.reference_number || null,
        ...sourceOf(g.rows[0]),
        items: [],
        allow_paid_removal: true,       // تأییدِ صریح در Popconfirm گرفته شده
      });
      message.success(r.data?.message || "دسته حذف شد");
      if (editBatch?.originalDate === g.date) cancelBatchEdit();
      loadSaved(); onDone?.();
    } catch (e) { message.error(apiError(e, "حذفِ دسته ناموفق بود")); }
    setDeletingBatch(null);
  };

  const [rows, setRows] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [method, setMethod] = useState("cash");
  const [ref, setRef] = useState("");
  // مبدأِ پولِ این دسته — دفتر (پیش‌فرض) یا تنخواه؛ سرور روی همهٔ ردیف‌ها می‌نشاند
  const [source, setSource] = useState(OFFICE);
  const [saving, setSaving] = useState(false);

  /* ویرایشِ **کلِ یک دسته**: همان کارتِ بالا دوباره پر می‌شود تا کاربر با همان
     ابزارِ آشنا کم/زیاد کند و یک‌جا ذخیره کند — خواستهٔ صریحِ کاربر:
     «ویرایش رو بزنیم لیست بالا بیاد که دوباره بتونیم کم کنیم، زیاد کنیم،
     مبلغ‌ها رو دسته‌جمعی ویرایش کنیم و بعد ثبت دسته‌جمعی رو بزنیم».
     `originalDate` نگه داشته می‌شود چون خودِ تاریخ هم قابلِ تغییر است و سرور
     باید بداند کدام دسته را جایگزین کند. */
  const [editBatch, setEditBatch] = useState(null);   // {originalDate, count}
  const startBatchEdit = (g) => {
    setSource(sourceOf(g.rows?.[0]));   // مبدأِ دسته حفظ می‌شود، نه پیش‌فرض
    setEditBatch({ originalDate: g.date, count: g.rows.length,
                   paidIds: g.rows.filter((t) => t.payment_status === "paid").map((t) => t.id) });
    setDate(g.date);
    setMethod(g.rows[0]?.payment_method || "cash");
    setRef(g.rows[0]?.reference_number || "");
    setRows(g.rows.map((t) => ({
      key: `t${t.id}`, id: t.id, employee_id: t.employee_id,
      amount: Number(t.amount) || null, note: t.description || "",
      paid: t.payment_status === "paid",
    })));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelBatchEdit = () => {
    setSource(OFFICE);
    setEditBatch(null); setRows([]); setRef(""); setDate(todayISO()); setMethod("cash");
  };

  /* اخطارِ کنارِ اسم (کاربر ۱۴۰۵/۰۷/۲۰): «وقتی افرادِ بیمه‌ای یا بدهکار را اضافه می‌کنیم، کنارِ اسمشان اخطار باشد که
     متوجه باشیم — شاید نخواهیم به این‌ها مساعده بدهیم». فقط هشدار است، جلوی ثبت را نمی‌گیرد.
     منبع: همان «مانده و وضعیت حساب» (بیمه‌ای/غیربیمه‌ای + ماندهٔ امروز؛ مثبت = بدهکار). */
  const [flags, setFlags] = useState({});
  useEffect(() => {
    getBalances({ include_settled: true })
      .then((r) => {
        const m = {};
        (r.data?.rows || []).forEach((x) => {
          m[x.employee_id] = { insured: x.insured_label === "بیمه‌ای", debt: Number(x.balance) > 0 ? Number(x.balance) : 0 };
        });
        setFlags(m);
      })
      .catch(() => {});
  }, []);
  const warnTags = (id) => {
    const f = flags[id] || {};
    const insured = f.insured ?? !!employees.find((e) => e.id === id)?.has_insurance;
    return (
      <>
        {insured && (
          <Tooltip title="این نفر بیمه‌ای است">
            <Tag color="orange" style={{ marginInlineStart: 4 }}>⚠ بیمه‌ای</Tag>
          </Tooltip>)}
        {f.debt > 0 && (
          <Tooltip title="ماندهٔ حسابِ امروز (مانده و وضعیت حساب)">
            <Tag color="red" style={{ marginInlineStart: 4 }}>⚠ بدهکار {faNum(Math.round(f.debt))} ریال</Tag>
          </Tooltip>)}
      </>
    );
  };

  /* مرتب‌سازیِ لیستِ هر دسته (کاربر ۱۴۰۵/۰۷/۲۵): «لیست‌ها را بر اساسِ هرچه بخواهیم سورت کنیم، بیمه‌ای هم اضافه شود، و
     فایلِ خروجی هم به همان ترتیب ساخته شود». ترتیب per دسته (تاریخ) نگه داشته می‌شود و `reFile` همان را می‌فرستد. */
  const [sortBy, setSortBy] = useState({});          // {date: {key, order}}
  const isInsured = (id) => flags[id]?.insured ?? !!employees.find((e) => e.id === id)?.has_insurance;
  const SORT_VAL = {
    name: (r) => r.employee_name || "",
    holder: (r) => r.account_holder || "",
    insured: (r) => (isInsured(r.employee_id) ? 0 : 1),
    amount: (r) => Number(r.amount) || 0,
    method: (r) => METHOD_LABEL[r.payment_method] || r.payment_method || "",
    status: (r) => (r.payment_status === "paid" ? 1 : 0),
    paid_at: (r) => String(r.paid_at || ""),
  };
  const cmp = (key) => (a, b) => {
    const x = SORT_VAL[key](a), y = SORT_VAL[key](b);
    return typeof x === "number" ? x - y : String(x).localeCompare(String(y), "fa");
  };
  const sortedRows = (g) => {
    const st = sortBy[g.date];
    if (!st?.order || !SORT_VAL[st.key]) return g.rows;
    const c = cmp(st.key);
    return [...g.rows].sort((a, b) => (st.order === "descend" ? -c(a, b) : c(a, b)));
  };

  const empOpts = useMemo(() => employees.map((e) => ({
    value: e.id, label: `${e.full_name} (${e.employee_code || "—"})`,
  })), [employees]);

  const addRow = (employee_id) => {
    if (!employee_id) return;
    if (rows.some((r) => r.employee_id === employee_id)) {
      message.warning("این نفر از قبل در لیست است");
      return;
    }
    setRows((p) => [...p, { key: `${employee_id}-${Date.now()}`, employee_id, amount: null, note: "" }]);
    const f = flags[employee_id] || {};
    const insured = f.insured ?? !!employees.find((e) => e.id === employee_id)?.has_insurance;
    const why = [insured && "بیمه‌ای است", f.debt > 0 && `${faNum(Math.round(f.debt))} ریال بدهکار است`].filter(Boolean);
    if (why.length) message.warning(`توجه: ${nameOf(employee_id)} ${why.join(" و ")}`, 5);
  };

  const patch = (key, v) => setRows((p) => p.map((r) => (r.key === key ? { ...r, ...v } : r)));

  const total = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  // ردیف‌های «پرداخت‌شده»ای که در ویرایش برداشته شده‌اند — حذفشان تأییدِ جدا می‌خواهد (از حسابِ شخص هم برمی‌دارد)
  const paidRemoved = editBatch ? (editBatch.paidIds || []).filter((id) => !rows.some((r) => r.id === id)) : [];
  const ready = rows.filter((r) => Number(r.amount) > 0);

  /* فایلِ پرداخت **قبل از ثبت** هم کار می‌کند: معمولاً اول پول را می‌دهند و
     بعد در سیستم ثبت می‌کنند، نه برعکس. */
  const [making, setMaking] = useState(false);
  const makeFile = async () => {
    if (!ready.length) return message.warning("هیچ ردیفی با مبلغ کامل نیست");
    setMaking(true);
    try {
      await downloadPaymentFile({
        title: "لیست پرداخت مساعده",
        subtitle: `${ready.length} نفر — تاریخ ${date || ""}`,
        items: ready.map((r) => ({
          employee_id: r.employee_id, amount: r.amount, note: r.note || "",
        })),
      });
      message.success("فایلِ پرداخت ساخته شد");
    } catch (e) { message.error(apiError(e, "ساختِ فایل ناموفق بود")); }
    setMaking(false);
  };

  const save = async () => {
    // در ویرایش، لیستِ خالی یعنی «کلِ دسته حذف شود» — قبلاً ذخیره رد می‌شد و دستهٔ اشتباه راهِ حذف نداشت
    if (!ready.length && !editBatch) return message.warning("هیچ ردیفی با مبلغ نیست");
    setSaving(true);
    if (editBatch) {
      try {
        const r = await updateAdvanceBatch({
          original_date: editBatch.originalDate,
          occurred_at: date,
          payment_method: method || null,
          reference_number: ref || null,
          ...source,
          payroll_period_id: periodId || null,
          items: ready.map((x) => ({
            id: x.id || null, employee_id: x.employee_id,
            amount: Number(x.amount), note: x.note || null,
          })),
          allow_paid_removal: paidRemoved.length > 0,
        });
        message.success(r.data.message);
        cancelBatchEdit(); loadSaved(); onDone?.();
      } catch (e) { message.error(apiError(e, "ویرایشِ دسته ناموفق بود")); }
      setSaving(false);
      return;
    }
    try {
      const r = await createAdvanceBatch({
        occurred_at: date,
        payment_method: method || null,
        reference_number: ref || null,
        ...source,
        payroll_period_id: periodId || null,
        items: ready.map((x) => ({
          employee_id: x.employee_id, amount: Number(x.amount), note: x.note || null,
        })),
      });
      message.success(r.data.message);
      setRows([]); setRef("");
      onDone?.();
    } catch (e) { message.error(apiError(e, "ثبت ناموفق بود")); }
    setSaving(false);
  };

  const nameOf = (id) => empOpts.find((o) => o.value === id)?.label || `#${id}`;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <GxNote id="advance-batch" signature="v1"
        message="نفرات را اضافه کنید، مبلغ بزنید، بعد یک‌جا ثبت کنید">
        ثبت <b>تراکنشی</b> است: یا همهٔ ردیف‌ها می‌نشینند یا هیچ‌کدام — پس لیستی که
        برای پرداخت چاپ می‌کنید همیشه با دفترِ مالی یکی است. هر مساعده یک
        <b> بدهکاری</b> برای همان نفر می‌سازد و در محاسبهٔ حقوقِ ماه از حقوقش کم
        می‌شود.
      </GxNote>

      {editBatch && (
        <Alert type="warning" showIcon
          message={`در حالِ ویرایشِ دستهٔ ${toJalali(editBatch.originalDate)}`}
          description="نفرات را کم/زیاد کنید و مبلغ‌ها را اصلاح کنید، بعد «ذخیرهٔ تغییرات» را بزنید. اگر ردیفِ «پرداخت‌شده» را بردارید، موقعِ ذخیره جدا تأیید می‌خواهد."
          action={<Button size="small" onClick={cancelBatchEdit}>انصراف</Button>} />
      )}

      <Card size="small" title={editBatch ? "مشخصاتِ دستهٔ در حالِ ویرایش" : "مشخصاتِ این دستهٔ مساعده"}>
        <Space wrap>
          <span>تاریخ:</span>
          <JalaliDateInput value={date} onChange={setDate} />
          <span>روشِ پرداخت:</span>
          <Select value={method} onChange={setMethod} style={{ width: 140 }}
            options={[
              { value: "cash", label: "نقدی" },
              { value: "bank_transfer", label: "کارت‌به‌کارت / حواله" },
              { value: "check", label: "چک" },
            ]} />
          <span>مبدأ:</span>
          <SourceSelect value={source} onChange={setSource} />
          <Input placeholder="شمارهٔ سند (اختیاری)" value={ref} style={{ width: 180 }}
            onChange={(e) => setRef(e.target.value)} />
        </Space>
      </Card>

      <Card size="small" title="نفرات"
        extra={
          <Space wrap>
            <Select showSearch style={{ width: 260 }} placeholder="افزودنِ پرسنل…"
              value={null} onChange={addRow} options={empOpts}
              optionRender={(o) => <span>{o.label}{warnTags(o.value)}</span>}
              optionFilterProp="label" filterOption={filterByChildren} />
            <Statistic title="جمعِ این دسته (ریال)" value={faNum(total)}
              valueStyle={{ fontSize: 18, color: "#1F4E79" }} />
            {/* همان فایلی که برای حقوق هست — چون مساعده هم از همان حساب‌ها
                پرداخت می‌شود و همان اطلاعاتِ بانکی را می‌خواهد. */}
            <Button icon={<FileExcelOutlined />} loading={making}
              disabled={!ready.length} onClick={makeFile}>
              فایلِ پرداخت (اکسل)
            </Button>
            <Popconfirm
              title={editBatch
                ? <>{ready.length
                  ? `دسته با ${faNum(ready.length)} نفر و جمعِ ${faNum(total)} ریال ذخیره شود؟`
                  : `لیست خالی است — کلِ دستهٔ ${toJalali(editBatch.originalDate)} حذف شود؟`}
                  {paidRemoved.length > 0 && (
                    <div style={{ color: "#C0392B", fontSize: 12, marginTop: 4 }}>
                      {faNum(paidRemoved.length)} ردیفِ «پرداخت‌شده» هم حذف می‌شود و از حسابِ آن نفر برداشته می‌شود.
                    </div>)}</>
                : `${faNum(ready.length)} مساعده به جمعِ ${faNum(total)} ریال ثبت شود؟`}
              okText={editBatch ? (ready.length ? "ذخیره" : "حذفِ دسته") : "ثبت"} cancelText="انصراف"
              okButtonProps={{ danger: !!editBatch && !ready.length }}
              onConfirm={save} disabled={!ready.length && !editBatch}>
              <Button type="primary" icon={editBatch && !ready.length ? <DeleteOutlined /> : <SaveOutlined />}
                danger={!!editBatch && !ready.length} loading={saving}
                disabled={!ready.length && !editBatch}>
                {editBatch ? (ready.length ? "ذخیرهٔ تغییرات" : "حذفِ کلِ دسته") : "ثبتِ دسته‌جمعی"}
              </Button>
            </Popconfirm>
          </Space>
        }>
        {rows.length === 0 ? (
          <Alert type={editBatch ? "warning" : "info"} showIcon
            message={editBatch
              ? "همهٔ نفرات برداشته شدند — با «حذفِ کلِ دسته» این دسته پاک می‌شود"
              : "هنوز کسی اضافه نشده — از کادرِ بالا پرسنل را انتخاب کنید"} />
        ) : (
          <Table size="small" pagination={false} rowKey="key" dataSource={rows}
            columns={[
              { title: "پرسنل", dataIndex: "employee_id",
                render: (v, r) => (
                  <Space size={4} wrap>{nameOf(v)}
                    {r.paid && <Tag color="green">پرداخت‌شده</Tag>}
                    {warnTags(v)}</Space>) },
              { title: "مبلغ (ریال)", dataIndex: "amount", width: 200,
                render: (v, r) => (
                  /* 🔴 پارسرِ دستیِ قبلی رقمِ فارسی را می‌خورد: `formatter` با
                     `faNum` رقمِ فارسی می‌ساخت و `parser` با `[^\d]` فیلترش
                     می‌کرد — و `\d` در جاوااسکریپت **فقط ۰-۹ لاتین** است. پس
                     هر رقمی که تایپ می‌شد بلافاصله پاک می‌شد و مبلغ وارد
                     نمی‌شد. `MoneyInput` از `moneyParser` استفاده می‌کند که
                     اول ارقام را لاتین می‌کند بعد فیلتر. */
                  <MoneyInput style={{ width: "100%" }} min={0} value={v}
                    onChange={(x) => patch(r.key, { amount: x })} />
                ) },
              { title: "توضیح", dataIndex: "note",
                render: (v, r) => (
                  <Input value={v} placeholder="اختیاری"
                    onChange={(e) => patch(r.key, { note: e.target.value })} />
                ) },
              { title: "", width: 50,
                render: (_, r) => (
                  /* ردیفِ پرداخت‌شده حذف نمی‌شود — پولش داده شده و در حسابِ
                     شخص نشسته. سرور هم همین را رد می‌کند؛ اینجا فقط زودتر و
                     با دلیل گفته می‌شود. */
                  <Tooltip title={r.paid ? "پرداخت شده — با ذخیره از حسابِ این نفر هم برداشته می‌شود" : "حذف از لیست"}>
                    <Button size="small" danger icon={<DeleteOutlined />}
                      onClick={() => setRows((p) => p.filter((x) => x.key !== r.key))} />
                  </Tooltip>
                ) },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><b>جمع ({faNum(ready.length)} نفر)</b></Table.Summary.Cell>
                <Table.Summary.Cell index={1}><b>{faNum(total)}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3} />
              </Table.Summary.Row>
            )} />
        )}
      </Card>

      {/* ── مساعده‌های ثبت‌شده ──
          خواستهٔ صریحِ کاربر: «ثبت دسته‌جمعی زدیم، فقط رفت تو هزینه‌ها نشست و
          هیچ چیزی نیاورد… می‌خوام دوباره لیست پرداختش رو بگیرم، نمی‌تونم.» */}
      <Card size="small" title={`مساعده‌های ثبت‌شده (${faNum(batches.length)} دسته)`}
        extra={<Button size="small" icon={<ReloadOutlined />} onClick={loadSaved}>بازخوانی</Button>}>
        {batches.length === 0 ? (
          <Alert type="info" showIcon message="هنوز مساعده‌ای ثبت نشده" />
        ) : (
          <Collapse accordion items={batches.map((g) => ({
            key: g.date,
            label: (
              <Space wrap>
                <b>{toJalali(g.date)}</b>
                <Tag>{faNum(g.rows.length)} نفر</Tag>
                <Tag color="blue">{faNum(g.total)} ریال</Tag>
                {g.pending > 0
                  ? <Tag color="orange">{faNum(g.pending)} در انتظارِ پرداخت</Tag>
                  : <Tag color="green">همه پرداخت شد</Tag>}
              </Space>
            ),
            extra: (
              <Space size={4}>
                {g.pending > 0 && (
                  <Button size="small" type="primary" loading={marking}
                    onClick={(e) => {
                      e.stopPropagation();
                      askPay(g.rows.filter((x) => x.payment_status !== "paid").map((x) => x.id));
                    }}>همه پرداخت شد</Button>
                )}
                <Button size="small" icon={<EditOutlined />}
                  onClick={(e) => { e.stopPropagation(); startBatchEdit(g); }}>
                  ویرایشِ دسته
                </Button>
                <Button size="small" icon={<FileExcelOutlined />}
                  loading={reDownloading === g.date}
                  onClick={(e) => { e.stopPropagation(); reFile(g); }}>
                  فایلِ پرداخت
                </Button>
                <span onClick={(e) => e.stopPropagation()}>
                  <Popconfirm
                    title={<>
                      {`کلِ دستهٔ ${toJalali(g.date)} (${faNum(g.rows.length)} نفر، ${faNum(g.total)} ریال) حذف شود؟`}
                      {g.rows.length > g.pending && (
                        <div style={{ color: "#C0392B", fontSize: 12, marginTop: 4 }}>
                          {faNum(g.rows.length - g.pending)} ردیف «پرداخت‌شده» است و از حسابِ آن نفرات هم برداشته می‌شود.
                        </div>)}
                    </>}
                    okText="حذف" cancelText="انصراف" okButtonProps={{ danger: true }}
                    onConfirm={() => deleteBatch(g)}>
                    <Button size="small" danger icon={<DeleteOutlined />}
                      loading={deletingBatch === g.date}>حذفِ دسته</Button>
                  </Popconfirm>
                </span>
              </Space>
            ),
            children: (
              <Table size="small" pagination={false} rowKey="id" dataSource={sortedRows(g)}
                scroll={{ x: "max-content" }}
                onChange={(_p, _f, sorter) => setSortBy((m) => ({
                  ...m, [g.date]: { key: sorter.columnKey, order: sorter.order || null } }))}
                columns={[
                  { title: "پرسنل", key: "name", dataIndex: "employee_id", sorter: true,
                    sortOrder: sortBy[g.date]?.key === "name" ? sortBy[g.date].order : null,
                    render: (v, r) => r.employee_name || nameOf(v) },
                  { title: "بیمه", key: "insured", width: 100, sorter: true,
                    sortOrder: sortBy[g.date]?.key === "insured" ? sortBy[g.date].order : null,
                    render: (_, r) => (isInsured(r.employee_id)
                      ? <Tag color="blue">بیمه‌ای</Tag> : <Tag>غیربیمه‌ای</Tag>) },
                  /* صاحبِ حساب کنارِ نام — خواستهٔ کاربر: «ممکنه حساب‌هاشون
                     متفاوت باشه». مقدار زنده از پروندهٔ پرسنل می‌آید. */
                  { title: "صاحب حساب", key: "holder", dataIndex: "account_holder", width: 150, sorter: true,
                    sortOrder: sortBy[g.date]?.key === "holder" ? sortBy[g.date].order : null,
                    render: (v, r) => (v && v !== (r.employee_name || nameOf(r.employee_id))
                      ? <b>{v}</b>
                      : <Text type="secondary">{v || "خودِ شخص"}</Text>) },
                  { title: "مبلغ (ریال)", key: "amount", dataIndex: "amount", align: "left", sorter: true,
                    sortOrder: sortBy[g.date]?.key === "amount" ? sortBy[g.date].order : null,
                    render: (v) => <CopyText copy={String(Math.round(Number(v) || 0))}>{faNum(v)}</CopyText> },
                  // v2.132.0: کپیِ شبا/کارت با یک کلیک
                  { title: "شبا", dataIndex: "iban", width: 250, render: (v) => <CopyText copy={v} mono>{v}</CopyText> },
                  { title: "کارت", dataIndex: "card_number", width: 190, render: (v) => <CopyText copy={v} mono>{v}</CopyText> },
                  { title: "روش", key: "method", dataIndex: "payment_method", sorter: true,
                    sortOrder: sortBy[g.date]?.key === "method" ? sortBy[g.date].order : null,
                    render: (v) => <span style={{ whiteSpace: "nowrap" }}>{METHOD_LABEL[v] || v || "—"}</span> },
                  { title: "مبدأ", dataIndex: "source_account", width: 130,
                    render: (v, r) => <Tag color={v === "petty" ? "geekblue" : "default"}>{sourceLabel(v, r.petty_account_id)}</Tag> },
                  { title: "وضعیت پرداخت", key: "status", dataIndex: "payment_status", sorter: true,
                    sortOrder: sortBy[g.date]?.key === "status" ? sortBy[g.date].order : null,
                    render: (v, r) => (v === "paid" ? (
                      <Space size={4} style={{ whiteSpace: "nowrap" }}>
                        <Tag color="green">پرداخت شد</Tag>
                        {r.paid_at && <Text type="secondary" style={{ fontSize: 11 }}>{toJalali(r.paid_at)}</Text>}
                      </Space>
                    ) : <Tag color="orange">در انتظارِ پرداخت</Tag>) },
                  { title: "اقدام",
                    render: (_, r) => (
                      <Space size={4}>
                        {r.payment_status === "paid" ? (
                          <Button size="small" loading={marking}
                            onClick={() => mark([r.id], false)}>برگرداندن</Button>
                        ) : (
                          <Button size="small" type="primary" loading={marking}
                            onClick={() => askPay([r.id])}>پرداخت شد</Button>
                        )}
                        <Popconfirm
                          title={`مساعدهٔ ${r.employee_name || nameOf(r.employee_id)} (${faNum(r.amount)} ریال) حذف شود؟`}
                          description={r.payment_status === "paid" ? "پرداخت شده — از حسابِ این نفر هم برداشته می‌شود." : null}
                          okText="حذف" cancelText="انصراف" okButtonProps={{ danger: true }}
                          onConfirm={() => removeOne(r)}>
                          <Tooltip title="حذفِ این مساعده">
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                          </Tooltip>
                        </Popconfirm>
                        <Tooltip title="ویرایشِ مبلغ، تاریخ و توضیح">
                          <Button size="small" type="text" icon={<EditOutlined />}
                            onClick={() => setEditRow({
                              id: r.id, amount: Number(r.amount) || null,
                              occurred_at: String(r.occurred_at || "").slice(0, 10),
                              description: r.description || "",
                              name: r.employee_name || nameOf(r.employee_id),
                            })} />
                        </Tooltip>
                      </Space>) },
                  { title: "توضیح", dataIndex: "description",
                    render: (v) => <span style={{ whiteSpace: "nowrap" }}>{v || "—"}</span> },
                ]}
                summary={() => (
                  /* ستونِ «مبلغ» چهارمی است (پرسنل، بیمه، صاحب حساب، مبلغ) — جمع باید
                     دقیقاً زیرِ همان بنشیند، وگرنه عدد زیرِ ستونِ اشتباه می‌افتد. */
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}><b>جمع</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="left">
                      <b>{faNum(g.total)}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={4} colSpan={7} />
                  </Table.Summary.Row>
                )} />
            ),
          }))} />
        )}
      </Card>

      {/* تاریخِ پرداخت — پیش‌فرض امروز، ولی قابلِ تغییر */}
      <Modal open={!!payAsk} onCancel={() => setPayAsk(null)}
        title={payAsk?.count > 1 ? `ثبتِ پرداختِ ${faNum(payAsk.count)} مورد` : "ثبتِ پرداخت"}
        okText="پرداخت شد" cancelText="انصراف" confirmLoading={marking}
        onOk={async () => { const a = payAsk; setPayAsk(null); await mark(a.ids, true, a.when, a.source); }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text type="secondary">پول چه روزی داده شد؟</Text>
          <JalaliDateInput value={payAsk?.when || todayISO()}
            onChange={(v) => setPayAsk((p) => (p ? { ...p, when: v || todayISO() } : p))} />
          <Text type="secondary">از کجا پرداخت شد؟</Text>
          <SourceSelect style={{ width: "100%" }} value={payAsk?.source || payAsk?.current}
            onChange={(v) => setPayAsk((p) => (p ? { ...p, source: v } : p))} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            همین تاریخ در دفترِ حساب و در فیشِ حقوقی می‌نشیند.
          </Text>
        </Space>
      </Modal>

      {/* ویرایشِ ردیفِ مساعده */}
      <Modal open={!!editRow} onCancel={() => setEditRow(null)}
        title={`ویرایشِ مساعده — ${editRow?.name || ""}`}
        okText="ذخیره" cancelText="انصراف" confirmLoading={savingEdit} onOk={saveEdit}>
        <Space direction="vertical" style={{ width: "100%" }} size={10}>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>مبلغ (ریال)</Text>
            <MoneyInput words style={{ width: "100%" }} value={editRow?.amount}
              onChange={(v) => setEditRow((r) => ({ ...r, amount: v }))} />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>تاریخ</Text>
            <div><JalaliDateInput value={editRow?.occurred_at}
              onChange={(v) => setEditRow((r) => ({ ...r, occurred_at: v }))} /></div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>توضیح</Text>
            <Input value={editRow?.description}
              onChange={(e) => setEditRow((r) => ({ ...r, description: e.target.value }))} />
          </div>
        </Space>
      </Modal>
    </Space>
  );
}
